import { useEffect, useState, useRef, useCallback } from "react";
import foxLogo from "../../assets/Fox Face and Paws.png";
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements,
} from "@stripe/react-stripe-js";
import Icon from "@mdi/react";
import { mdiCheckCircle, mdiAlertCircle, mdiLockOutline } from "@mdi/js";
import { useAuth } from "../../contexts/AuthContext";
import { bookingApi } from "../../api/booking";
import SupplierPolicyAcknowledgement, { policyToSections } from "../../components/SupplierPolicyAcknowledgement";
import { paymentApi } from "../../api/payment";
import { formatCurrencyFromMinor as formatCurrencyValue } from "../../utils/currency";
import { isOrgPaymentsNotReady, orgPaymentsNotReadyMessage } from "../../utils/orgPayments";
import "../../style.css";
import { getStripePromise } from "../../utils/stripeKey";
import ServiceFeeInfo from "../../components/ServiceFeeInfo";

// Preserve the pre-migration empty-state behaviour: missing amount renders "—",
// a real 0 still formats as A$0.00.
const formatCurrency = (amount, currency) =>
    amount == null ? "—" : formatCurrencyValue(amount, currency);


/**
 * PaymentForm - Stripe Elements card form rendered inside an Elements provider.
 * Receives the Stripe clientSecret from the parent and handles payment confirmation.
 */
function PaymentForm({ booking, onSuccess, onError }) {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [paymentError, setPaymentError] = useState("");
    const [elementsReady, setElementsReady] = useState(false);
    const [policyAck, setPolicyAck] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements || !elementsReady) return;
        if (!policyAck) return;

        setIsProcessing(true);
        setPaymentError("");

        try {
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                redirect: "if_required",
            });

            if (error) {
                setPaymentError(error.message || "Payment failed. Please try again.");
                setIsProcessing(false);
                onError(error.message);
                return;
            }

            if (paymentIntent && paymentIntent.status === "succeeded") {
                onSuccess(paymentIntent.id);
            } else {
                setPaymentError("Payment not completed. Please try again.");
                setIsProcessing(false);
            }
        } catch (err) {
            setPaymentError(err.message || "Payment failed. Please try again.");
            setIsProcessing(false);
            onError(err.message);
        }
    };

    return (
        <form onSubmit={handleSubmit} style={styles.form}>
            {/* Booking Summary */}
            <div style={styles.summaryCard}>
                <h3 style={styles.summaryTitle}>Lesson Payment</h3>
                {booking && (
                    <>
                        <div style={styles.summaryRow}>
                            <span style={styles.summaryLabel}>Date</span>
                            <span style={styles.summaryValue}>
                                {new Date(booking.bookingDate).toLocaleDateString("en-AU", {
                                    weekday: "long",
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                })}
                            </span>
                        </div>
                        <div style={styles.summaryRow}>
                            <span style={styles.summaryLabel}>Time</span>
                            <span style={styles.summaryValue}>
                                {booking.timeSlot?.startTime} – {booking.timeSlot?.endTime}
                            </span>
                        </div>
                        {booking.pricing?.artistFeeSubtotal > 0 && (
                            <div style={styles.summaryRow}>
                                <span style={styles.summaryLabel}>Artist fee</span>
                                <span style={styles.summaryValue}>
                                    {formatCurrency(booking.pricing.artistFeeSubtotal, booking.pricing.currency)}
                                </span>
                            </div>
                        )}
                        {booking.pricing?.venueHireSubtotal > 0 && (
                            <div style={styles.summaryRow}>
                                <span style={styles.summaryLabel}>Venue hire</span>
                                <span style={styles.summaryValue}>
                                    {formatCurrency(booking.pricing.venueHireSubtotal, booking.pricing.currency)}
                                </span>
                            </div>
                        )}
                        {(booking.pricing?.platformFeeSubtotal > 0 || booking.pricing?.processingFeeSubtotal > 0) && (
                            <div style={styles.summaryRow}>
                                <ServiceFeeInfo labelStyle={styles.summaryLabel} />
                                <span style={styles.summaryValue}>
                                    {formatCurrency(
                                        (booking.pricing.platformFeeSubtotal || 0) + (booking.pricing.processingFeeSubtotal || 0),
                                        booking.pricing.currency
                                    )}
                                </span>
                            </div>
                        )}
                        <div style={{ ...styles.summaryRow, ...styles.totalRow }}>
                            <span style={styles.totalLabel}>Total</span>
                            <span style={styles.totalValue}>
                                {formatCurrency(booking.pricing?.amount, booking.pricing?.currency)}
                            </span>
                        </div>
                    </>
                )}
            </div>

            {booking?.privateLessonPolicy && (
                <SupplierPolicyAcknowledgement
                    sections={policyToSections(booking.privateLessonPolicy)}
                    supplierName={booking.instructorName}
                    surfaceLabel="lesson"
                    acknowledged={policyAck}
                    onAcknowledgedChange={setPolicyAck}
                />
            )}

            {/* Stripe Payment Element */}

            <div style={styles.paymentElementWrapper}>
                <PaymentElement onReady={() => setElementsReady(true)} onLoadError={(e) => setPaymentError(e?.error?.message || "Could not load the payment form. Please try again.")} options={{ layout: "tabs", paymentMethodOrder: ["apple_pay", "google_pay", "card"] }} />
            </div>

            {paymentError && (
                <div style={styles.errorBanner}>
                    {paymentError}
                </div>
            )}

            <button
                type="submit"
                disabled={!stripe || !elements || !elementsReady || isProcessing || !policyAck}
                style={{
                    ...styles.payButton,
                    opacity: (!stripe || !elements || !elementsReady || isProcessing || !policyAck) ? 0.6 : 1,
                    cursor: (!stripe || !elements || !elementsReady || isProcessing || !policyAck) ? "not-allowed" : "pointer",
                }}
            >
                {isProcessing ? "Processing…" : `Pay ${formatCurrency(booking?.pricing?.amount, booking?.pricing?.currency)}`}
            </button>

            <p style={styles.secureNote}>
                <Icon path={mdiLockOutline} size={0.55} style={{ verticalAlign: "middle", marginRight: "4px" }} />
                Secured by Stripe. Your payment details are never stored on our servers.
            </p>
        </form>
    );
}

/**
 * BookingPayment - Mobile-to-Web Booking Payment Page
 *
 * Flow:
 * 1. Validates booking session (bs_ prefix) and auto-authenticates user
 * 2. Fetches booking details
 * 3. Creates a Stripe PaymentIntent via POST /api/v2/payment/create
 * 4. Renders Stripe Elements payment form
 * 5. On success, deep links back to mobile app
 */
const BookingPayment = () => {
    const { login } = useAuth();

    const [status, setStatus] = useState("initializing"); // initializing | authenticated | paying | success | error
    const [error, setError] = useState("");
    const [booking, setBooking] = useState(null);
    const [clientSecret, setClientSecret] = useState(null);
    const [, setReturnApp] = useState("mobile");
    const [branding, setBranding] = useState({});
    const [orgName, setOrgName] = useState('');

    const sessionValidatedRef = useRef(false);

    const handleReturnToApp = useCallback(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const bookingId = urlParams.get("bookingId");
        if (bookingId) {
            window.location.replace(`https://dancelocale.com/bookings/${bookingId}/details`);
        } else {
            window.location.replace("https://dancelocale.com/bookings");
        }
    }, []);

    useEffect(() => {
        const initPaymentFlow = async () => {
            if (sessionValidatedRef.current) return;

            try {
                sessionValidatedRef.current = true;

                const urlParams = new URLSearchParams(window.location.search);

                // Demo mode — skip session validation, use mock data
                if (urlParams.get("demo") === "true") {
                    const demoOrgId = "690eaebea99d5c88490dcca6";
                    setBooking({
                        id: "demo",
                        instructorId: demoOrgId,
                        instructorName: "Foxy Roxy Dance",
                        orgName: "Foxy Roxy Dance",
                        studentName: "Sarah Mitchell",
                        bookingDate: "2026-08-09T19:00:00",
                        timeSlot: { startTime: "10:00", endTime: "11:00" },
                        pricing: {
                            artistFeeSubtotal: 8000,
                            venueHireSubtotal: 2000,
                            platformFeeSubtotal: 300,
                            processingFeeSubtotal: 193,
                            amount: 10493,
                            currency: "AUD",
                        },
                    });
                    const { orgName: demoOrgName, branding: demoBranding } =
                        await paymentApi.getOrgBrandingPublic(demoOrgId);
                    setBranding(demoBranding);
                    setOrgName(demoOrgName || "Foxy Roxy Dance");
                    setStatus("paying");
                    return;
                }

                setStatus("initializing");

                const sessionId = urlParams.get("sessionId");
                const bookingId = urlParams.get("bookingId");
                const returnAppParam = urlParams.get("returnApp") || "mobile";

                setReturnApp(returnAppParam);

                const isDevSession = sessionId?.startsWith("bs_dev_");
                if (isDevSession) {
                    localStorage.setItem("dance-locale-dev-mode", "true");
                } else {
                    localStorage.removeItem("dance-locale-dev-mode");
                }

                if (!sessionId || !bookingId) {
                    setError("Invalid payment link. Please try again from the app.");
                    setStatus("error");
                    return;
                }

                // Step 1: Validate session → get JWT tokens
                setStatus("authenticating");
                const sessionResponse = await bookingApi.validateBookingSession(sessionId);

                if (!sessionResponse.ok || !sessionResponse.data?.success) {
                    let msg = "Your payment session has expired. Go back to the app and tap Pay Now to try again.";
                    if (sessionResponse.status === 404) msg = "Invalid session. Go back to the app and tap Pay Now to try again.";
                    if (sessionResponse.status === 409) msg = "This payment link has already been used. Go back to the app and tap Pay Now to try again.";
                    if (sessionResponse.status === 410) msg = "Your session has expired. Go back to the app and tap Pay Now to try again.";
                    setError(msg);
                    setStatus("error");
                    return;
                }

                const { accessToken, refreshToken, user } = sessionResponse.data.session;
                localStorage.setItem("accessToken", accessToken);
                localStorage.setItem("refreshToken", refreshToken);
                login(user);

                // Step 2: Fetch booking details
                let bookingData;
                try {
                    bookingData = await bookingApi.getBookingDetails(bookingId);
                } catch (err) {
                    setError("Could not load booking details. Please try again.");
                    setStatus("error");
                    return;
                }
                setBooking(bookingData);

                // Step 2.5: Fetch org branding (graceful — payment page renders even if this fails)
                const { orgName: fetchedOrgName, branding: fetchedBranding } =
                    await paymentApi.getOrgBrandingPublic(bookingData.instructorId);
                setBranding(fetchedBranding);
                setOrgName(fetchedOrgName || bookingData.instructorName || '');

                // Step 3: Create Stripe PaymentIntent
                let paymentData;
                try {
                    paymentData = await paymentApi.createPaymentIntent({
                        sourceType: "booking",
                        sourceId: bookingId,
                        organisationId: bookingData.instructorId,
                        // pricing.* are already integer cents — createPaymentIntent expects cents.
                        artistFeeSubtotal: bookingData.pricing?.artistFeeSubtotal || 0,
                        venueHireSubtotal: bookingData.pricing?.venueHireSubtotal || 0,
                        currency: bookingData.pricing?.currency || "AUD",
                    });
                } catch (err) {
                    setError(isOrgPaymentsNotReady(err)
                        ? orgPaymentsNotReadyMessage(fetchedOrgName || bookingData.instructorName, "your instructor")
                        : (err.message || "Failed to initialise payment. Please try again."));
                    setStatus("error");
                    return;
                }

                setClientSecret(paymentData.paymentIntent.clientSecret);
                setStatus("paying");

            } catch (err) {
                console.error("BookingPayment - Error:", err);
                setError("An unexpected error occurred. Please try again from the app.");
                setStatus("error");
            }
        };

        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("sessionId") || urlParams.get("demo") === "true") {
            initPaymentFlow();
        } else {
            setError("No payment session found. Please use the app to pay for your lesson.");
            setStatus("error");
        }
    }, [login]);

    // Apply org branding CSS vars to page root after render
    useEffect(() => {
        const root = document.getElementById('booking-payment-root');
        if (!root || !branding) return;
        if (branding.buttonColor)     root.style.setProperty('--school-btn-color', branding.buttonColor);
        if (branding.buttonTextColor) root.style.setProperty('--school-btn-text', branding.buttonTextColor);
        if (branding.headerColor)     root.style.setProperty('--school-header-color', branding.headerColor);
        if (branding.headerTextColor) root.style.setProperty('--school-header-text', branding.headerTextColor);
    }, [branding]);

    const handlePaymentSuccess = async () => {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const bid = urlParams.get("bookingId");
            await bookingApi.recordPayment(bid);
        } catch (err) {
            console.error("BookingPayment - Failed to record payment on backend:", err);
        }
        setStatus("success");
    };

    const handlePaymentError = (message) => {
        // Error is shown inside PaymentForm — no status change needed here
        console.error("Payment error:", message);
    };

    // Loading / transition states
    if (status === "initializing" || status === "authenticating") {
        return (
            <div style={styles.fullPage}>
                <div style={styles.centeredBox}>
                    <div style={styles.spinner} />
                    <h2 style={styles.loadingTitle}>
                        {status === "initializing" ? "Setting up payment…" : "Authenticating…"}
                    </h2>
                    <p style={styles.loadingMessage}>
                        {status === "initializing"
                            ? "Preparing your secure payment session"
                            : "Verifying your session automatically"}
                    </p>
                </div>
            </div>
        );
    }

    if (status === "success") {
        return (
            <div style={styles.fullPage}>
                <div style={styles.centeredBox}>
                    <div style={styles.successIcon}>
                        <Icon path={mdiCheckCircle} size={2} color="#fff" />
                    </div>
                    <h2 style={styles.loadingTitle}>Payment Successful!</h2>
                    <p style={styles.loadingMessage}>
                        Your lesson has been paid.<br /><br /><strong>Close</strong> this window to return to where you came from.
                    </p>
                </div>
            </div>
        );
    }

    if (status === "error") {
        return (
            <div style={styles.fullPage}>
                <div style={styles.centeredBox}>
                    <div style={styles.errorIcon}>
                        <Icon path={mdiAlertCircle} size={2.5} color="#fff" />
                    </div>
                    <h2 style={styles.errorTitle}>Payment Error</h2>
                    <p style={styles.loadingMessage}>{error}</p>
                    <div style={styles.buttonRow}>
                        <button onClick={handleReturnToApp} style={styles.returnButton}>
                            Go Back to App
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Payment form state — card layout
    const isDemoBooking = booking?.id === "demo";
    const stripeColorPrimary = branding?.buttonColor || branding?.headerColor || "#451f78";
    return (
        <div style={styles.paymentPage} id="booking-payment-root">
            {/* School-branded header card */}
            <div style={styles.paymentHeaderCard}>
                <h1 style={styles.paymentHeaderTitle}>{orgName || "Lesson Payment"}</h1>
                <p style={styles.paymentHeaderSubtitle}>Lesson Payment</p>
                {booking?.studentName && (
                    <p style={styles.paymentHeaderStudentName}>For {booking.studentName}</p>
                )}
            </div>

            {/* White content card */}
            <div style={styles.paymentBody}>
                {isDemoBooking ? (
                    <>
                        <div style={styles.summaryCard}>
                            <h3 style={styles.summaryTitle}>Lesson Summary</h3>
                            <div style={styles.summaryRow}>
                                <span style={styles.summaryLabel}>Date</span>
                                <span style={styles.summaryValue}>Saturday, 9 August 2026</span>
                            </div>
                            <div style={styles.summaryRow}>
                                <span style={styles.summaryLabel}>Time</span>
                                <span style={styles.summaryValue}>10:00 – 11:00</span>
                            </div>
                            <div style={styles.summaryRow}>
                                <span style={styles.summaryLabel}>Artist fee</span>
                                <span style={styles.summaryValue}>
                                    {formatCurrency(booking.pricing.artistFeeSubtotal, booking.pricing.currency)}
                                </span>
                            </div>
                            <div style={styles.summaryRow}>
                                <span style={styles.summaryLabel}>Venue hire</span>
                                <span style={styles.summaryValue}>
                                    {formatCurrency(booking.pricing.venueHireSubtotal, booking.pricing.currency)}
                                </span>
                            </div>
                            <div style={styles.summaryRow}>
                                <ServiceFeeInfo labelStyle={styles.summaryLabel} />
                                <span style={styles.summaryValue}>
                                    {formatCurrency(
                                        (booking.pricing.platformFeeSubtotal || 0) + (booking.pricing.processingFeeSubtotal || 0),
                                        booking.pricing.currency
                                    )}
                                </span>
                            </div>
                            <div style={{ ...styles.summaryRow, ...styles.totalRow }}>
                                <span style={styles.totalLabel}>Total</span>
                                <span style={styles.totalValue}>
                                    {formatCurrency(booking.pricing.amount, booking.pricing.currency)}
                                </span>
                            </div>
                        </div>
                        <div style={{ border: "2px dashed var(--dl-color-lightplus)", borderRadius: "8px", padding: "24px", textAlign: "center", color: "var(--dl-color-text-muted)", background: "var(--dl-color-gray-50)", margin: "20px 0" }}>
                            <p style={{ margin: 0, fontWeight: 600 }}>Stripe Payment Form</p>
                            <p style={{ margin: "4px 0 0", fontSize: "0.85rem" }}>Card / Apple Pay / Google Pay would appear here</p>
                        </div>
                        <button style={{ ...styles.payButton, opacity: 0.6, cursor: "not-allowed" }} disabled>Pay {formatCurrency(booking.pricing.amount, booking.pricing.currency)}</button>
                    </>
                ) : clientSecret && (
                    <Elements
                        stripe={getStripePromise()}
                        options={{
                            clientSecret,
                            appearance: {
                                theme: "stripe",
                                variables: {
                                    colorPrimary: stripeColorPrimary,
                                    colorBackground: "#ffffff",
                                    colorText: "#1a1a1a",
                                    borderRadius: "8px",
                                    fontFamily: "system-ui, sans-serif",
                                },
                            },
                        }}
                    >
                        <PaymentForm
                            booking={booking}
                            onSuccess={handlePaymentSuccess}
                            onError={handlePaymentError}
                        />
                    </Elements>
                )}

                <div style={{ textAlign: 'right', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid var(--dl-color-border)' }}>
                    <button onClick={handleReturnToApp} style={styles.cancelLink}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

const styles = {
    fullPage: {
        minHeight: "100vh",
        background: "linear-gradient(135deg, var(--dl-color-purple-default) 0%, var(--dl-color-turquoise-default) 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        fontFamily: "system-ui, -apple-system, sans-serif",
    },
    centeredBox: {
        textAlign: "center",
        color: "var(--dl-color-gray-white)",
        maxWidth: "400px",
        width: "100%",
    },
    spinner: {
        border: "3px solid rgba(255,255,255,0.3)",
        borderRadius: "50%",
        borderTop: "3px solid #fff",
        width: "40px",
        height: "40px",
        animation: "spin 1s linear infinite",
        margin: "0 auto 24px",
    },
    loadingTitle: {
        fontSize: "1.4rem",
        fontWeight: 600,
        marginBottom: "12px",
        color: "var(--dl-color-gray-white)",
    },
    loadingMessage: {
        fontSize: "1rem",
        opacity: 0.9,
        lineHeight: 1.5,
        color: "var(--dl-color-gray-white)",
    },
    successIcon: {
        width: "64px",
        height: "64px",
        background: "rgba(255,255,255,0.2)",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "2rem",
        margin: "0 auto 24px",
        color: "var(--dl-color-gray-white)",
    },
    errorIcon: {
        marginBottom: "16px",
    },
    errorTitle: {
        fontSize: "1.4rem",
        fontWeight: 600,
        marginBottom: "12px",
        color: "var(--dl-color-gray-white)",
    },
    buttonRow: {
        display: "flex",
        gap: "12px",
        justifyContent: "center",
        flexWrap: "wrap",
        marginTop: "24px",
    },
    returnButton: {
        padding: "12px 24px",
        background: "rgba(255,255,255,0.2)",
        border: "2px solid rgba(255,255,255,0.5)",
        color: "var(--dl-color-gray-white)",
        borderRadius: "8px",
        cursor: "pointer",
        fontSize: "0.9rem",
        fontWeight: 500,
    },
    secondaryButton: {
        padding: "12px 24px",
        background: "transparent",
        border: "2px solid rgba(255,255,255,0.3)",
        color: "var(--dl-color-gray-white)",
        borderRadius: "8px",
        cursor: "pointer",
        fontSize: "0.9rem",
        fontWeight: 500,
    },
    // Card layout styles for payment state
    paymentPage: {
        minHeight: "100vh",
        background: "var(--dl-color-gray-200)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "40px 16px",
        fontFamily: "system-ui, -apple-system, sans-serif",
    },
    paymentHeaderCard: {
        width: "100%",
        maxWidth: "480px",
        background: "var(--school-header-color, var(--dl-color-purple-default))",
        color: "var(--school-header-text, var(--dl-color-gray-white))",
        borderRadius: "12px 12px 0 0",
        padding: "24px",
        textAlign: "center",
    },
    paymentHeaderTitle: {
        fontSize: "1.3rem",
        fontWeight: 700,
        margin: "0 0 4px",
    },
    paymentHeaderSubtitle: {
        fontSize: "0.9rem",
        opacity: 0.85,
        margin: 0,
    },
    paymentHeaderStudentName: {
        fontSize: "0.8rem",
        opacity: 0.7,
        fontStyle: "italic",
        margin: "6px 0 0",
    },
    paymentBody: {
        background: "var(--dl-color-gray-white)",
        borderRadius: "0 0 12px 12px",
        padding: "24px",
        maxWidth: "480px",
        width: "100%",
        boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
        boxSizing: "border-box",
    },
    // Legacy — kept for loading/error/success states
    paymentContainer: {
        background: "var(--dl-color-gray-white)",
        borderRadius: "16px",
        padding: "32px 24px",
        maxWidth: "480px",
        width: "100%",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
    },
    header: {
        textAlign: "center",
        marginBottom: "28px",
    },
    logo: {
        width: "96px",
        height: "96px",
        objectFit: "contain",
        display: "block",
        margin: "0 auto 4px",
    },
    headerTitle: {
        fontSize: "1.4rem",
        fontWeight: 700,
        color: "var(--dl-color-purple-default)",
        margin: "0 0 4px",
    },
    headerSubtitle: {
        fontSize: "0.9rem",
        color: "var(--dl-color-gray-600)",
        margin: 0,
    },
    form: {
        display: "flex",
        flexDirection: "column",
        gap: "20px",
    },
    summaryCard: {
        background: "var(--dl-color-purple-50)",
        borderRadius: "10px",
        padding: "16px",
        border: "1px solid var(--dl-color-purple-200)",
    },
    summaryTitle: {
        fontSize: "1rem",
        fontWeight: 600,
        color: "var(--dl-color-purple-default)",
        margin: "0 0 12px",
    },
    summaryRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "8px",
    },
    summaryLabel: {
        fontSize: "0.875rem",
        color: "var(--dl-color-gray-600)",
    },
    summaryValue: {
        fontSize: "0.875rem",
        color: "var(--dl-color-text-primary)",
        fontWeight: 500,
        textAlign: "right",
        maxWidth: "60%",
    },
    totalRow: {
        borderTop: "1px solid var(--dl-color-purple-200)",
        paddingTop: "10px",
        marginTop: "6px",
        marginBottom: 0,
    },
    totalLabel: {
        fontSize: "1rem",
        fontWeight: 700,
        color: "var(--dl-color-text-primary)",
    },
    totalValue: {
        fontSize: "1.1rem",
        fontWeight: 700,
        color: "var(--dl-color-purple-default)",
    },
    walletHint: {
        fontSize: "0.78rem",
        color: "var(--dl-color-gray-500)",
        textAlign: "center",
        margin: "0 0 12px",
        padding: "8px 0",
        borderBottom: "1px solid var(--dl-color-gray-100)",
    },
    paymentElementWrapper: {
        padding: "4px 0",
    },
    errorBanner: {
        background: "var(--dl-color-gray-50)",
        border: "1px solid #ffcdd2",
        color: "var(--dl-color-warning-red)",
        borderRadius: "8px",
        padding: "12px 16px",
        fontSize: "0.875rem",
    },
    payButton: {
        background: "var(--school-btn-color, var(--dl-color-purple-default))",
        color: "var(--school-btn-text, var(--dl-color-gray-white))",
        border: "none",
        borderRadius: "10px",
        padding: "16px",
        fontSize: "1rem",
        fontWeight: 600,
        width: "100%",
        transition: "opacity 0.2s ease",
    },
    secureNote: {
        textAlign: "center",
        fontSize: "0.75rem",
        color: "var(--dl-color-gray-400)",
        margin: 0,
    },
    cancelLink: {
        background: "none",
        border: "none",
        color: "var(--dl-color-gray-400)",
        fontSize: "0.85rem",
        cursor: "pointer",
        padding: "4px 0",
    },
};

// Inject keyframe for spinner (once, outside component)
if (typeof document !== "undefined" && !document.getElementById("booking-payment-keyframes")) {
    const style = document.createElement("style");
    style.id = "booking-payment-keyframes";
    style.textContent = `@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`;
    document.head.appendChild(style);
}

export default BookingPayment;
